// Per-user settings (DEVELOPMENT.md: Data model → User, Screens → Settings).

export type DigitSetDefault = '1-9' | '0-9' | 'letters';
export type InputMode = 'sudokupad' | 'simple';
export type MistakeChecking = 'off' | 'conflicts' | 'solution';
/** The digit pad: blocks (3×3, beside the mode buttons) or one row (two for 16 symbols). */
export type PadLayout = 'blocks' | 'row';
/** With the row layout: the mode buttons above or below the numbers. */
export type PadToolsAt = 'above' | 'below';
/** The pad's modes, each with a look of its own (Simple mode's pencil marks too). */
export type PadLookMode = 'digit' | 'corner' | 'centre' | 'position' | 'pencil';
/** Where a number sits on its key: big, small top left, small in the middle, or small in its
 * own spot (1 top left, 9 bottom right). */
export type PadNumberStyle = 'big' | 'corner' | 'centre' | 'position';
export interface PadLook {
  style: PadNumberStyle;
  /** Key background and number colour, `#rrggbb`; null: the app's own. */
  key: string | null;
  number: string | null;
}
export type PadLooks = Record<PadLookMode, PadLook>;

export const PAD_LOOK_MODES: readonly PadLookMode[] = [
  'digit',
  'corner',
  'centre',
  'position',
  'pencil',
];

/** The keys show what a press makes (Marko, 2026-11-01); colours are each person's choice. */
export const DEFAULT_PAD_LOOKS: PadLooks = {
  digit: { style: 'big', key: null, number: null },
  corner: { style: 'corner', key: null, number: null },
  centre: { style: 'centre', key: null, number: null },
  position: { style: 'position', key: null, number: null },
  pencil: { style: 'position', key: null, number: null },
};

export interface UserSettings {
  digits: DigitSetDefault;
  defaultMode: InputMode;
  mistakeChecking: MistakeChecking;
  /** Entered digits checked as you go, in both modes, at the mistake-checking level. */
  checkDigits: boolean;
  /** Corner, centre and pencil marks checked as you go, at the mistake-checking level. */
  checkMarks: boolean;
  clearNotesOnPlacement: boolean;
  highlightMatchingDigits: boolean;
  /** Cells whose corner or centre marks hold the selected digit, in both modes (Marko, 2026-11-01). */
  highlightMatchingCandidates: boolean;
  /** One tap on a cell with a digit highlights it everywhere, with its candidates, as a double tap
   * does; only the tapped cell is selected (both modes; off by default: Marko's wife, 2026-11-01). */
  tapHighlightsDigit: boolean;
  highlightHouses: boolean;
  hideTimer: boolean;
  keepScreenOn: boolean;
  /** The "Fill all candidates" button (1–9) by undo and redo (on by default). */
  showFillButton: boolean;
  /** The settings cog by undo and redo (on by default; Marko, 2026-11-03). Hidden, the puzzle's
   * settings are still in the ⋯ menu. */
  showSettingsButton: boolean;
  /** The select tool next to the pad: while on, taps add and remove cells (on by default). */
  showSelectTool: boolean;
  /** SudokuPad mode's colour tool next to the pad (off by default: Marko, 2026-11-01). */
  showColourTool: boolean;
  /** SudokuPad mode's pen (line) tool next to the pad (off by default). */
  showPenTool: boolean;
  /** SudokuPad mode's mark buttons (all on by default; Marko, 2026-11-01). A hidden one leaves
   * the pad and Space's cycle; its keys (X, C, B, Shift, Ctrl) still work. */
  showCornerMarks: boolean;
  showCentreMarks: boolean;
  showPositionMarks: boolean;
  padLayout: PadLayout;
  padToolsAt: PadToolsAt;
  padLooks: PadLooks;
}

export const DEFAULT_SETTINGS: UserSettings = {
  digits: '1-9',
  defaultMode: 'sudokupad',
  mistakeChecking: 'conflicts',
  checkDigits: true,
  checkMarks: true,
  clearNotesOnPlacement: false,
  highlightMatchingDigits: true,
  highlightMatchingCandidates: true,
  tapHighlightsDigit: false,
  highlightHouses: true,
  hideTimer: false,
  keepScreenOn: true,
  showFillButton: true,
  showSettingsButton: true,
  showSelectTool: true,
  showColourTool: false,
  showPenTool: false,
  showCornerMarks: true,
  showCentreMarks: true,
  showPositionMarks: true,
  padLayout: 'blocks',
  padToolsAt: 'below',
  padLooks: DEFAULT_PAD_LOOKS,
};

const DIGITS: readonly DigitSetDefault[] = ['1-9', '0-9', 'letters'];
const MODES: readonly InputMode[] = ['sudokupad', 'simple'];
const CHECKING: readonly MistakeChecking[] = ['off', 'conflicts', 'solution'];
const LAYOUTS: readonly PadLayout[] = ['blocks', 'row'];
const TOOLS_AT: readonly PadToolsAt[] = ['above', 'below'];
const NUMBER_STYLES: readonly PadNumberStyle[] = ['big', 'corner', 'centre', 'position'];
const HEX = /^#[0-9a-f]{6}$/i;
const FLAGS = [
  'checkDigits',
  'checkMarks',
  'clearNotesOnPlacement',
  'highlightMatchingDigits',
  'highlightMatchingCandidates',
  'tapHighlightsDigit',
  'highlightHouses',
  'hideTimer',
  'keepScreenOn',
  'showFillButton',
  'showSettingsButton',
  'showSelectTool',
  'showColourTool',
  'showPenTool',
  'showCornerMarks',
  'showCentreMarks',
  'showPositionMarks',
] as const;

/** Stored pad looks over the defaults, mode by mode; invalid styles and colours fall back. */
export function normalizePadLooks(stored: unknown): PadLooks {
  const s = (typeof stored === 'object' && stored !== null ? stored : {}) as Record<
    string,
    unknown
  >;
  const out = {} as PadLooks;
  for (const mode of PAD_LOOK_MODES) {
    const look = (typeof s[mode] === 'object' && s[mode] !== null ? s[mode] : {}) as Record<
      string,
      unknown
    >;
    const colour = (v: unknown) => (typeof v === 'string' && HEX.test(v) ? v.toLowerCase() : null);
    out[mode] = {
      style: NUMBER_STYLES.includes(look.style as PadNumberStyle)
        ? (look.style as PadNumberStyle)
        : DEFAULT_PAD_LOOKS[mode].style,
      key: colour(look.key),
      number: colour(look.number),
    };
  }
  return out;
}

/** Stored settings merged over the defaults; unknown or invalid values fall back. */
export function normalizeSettings(stored: unknown): UserSettings {
  const s = (typeof stored === 'object' && stored !== null ? stored : {}) as Record<
    string,
    unknown
  >;
  const out: UserSettings = { ...DEFAULT_SETTINGS };
  if (DIGITS.includes(s.digits as DigitSetDefault)) out.digits = s.digits as DigitSetDefault;
  if (MODES.includes(s.defaultMode as InputMode)) out.defaultMode = s.defaultMode as InputMode;
  if (CHECKING.includes(s.mistakeChecking as MistakeChecking)) {
    out.mistakeChecking = s.mistakeChecking as MistakeChecking;
  }
  for (const flag of FLAGS) {
    if (typeof s[flag] === 'boolean') out[flag] = s[flag];
  }
  if (LAYOUTS.includes(s.padLayout as PadLayout)) out.padLayout = s.padLayout as PadLayout;
  if (TOOLS_AT.includes(s.padToolsAt as PadToolsAt)) out.padToolsAt = s.padToolsAt as PadToolsAt;
  out.padLooks = normalizePadLooks(s.padLooks);
  return out;
}
